import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { useAtom } from "jotai";
import { Button } from "./Controls/Button";
import { ReserveInitResp } from "./Data/ApiTypes";
import { showMoney, toCents } from "./Data/Money";
import { showDuration } from "./Data/Date";
import { maskAtom, PageMaskOpts } from "./Data/Atoms";
import { Api } from "./Data/Api";
import { connectUrl } from "./Data/WebUrl";
import { createApiService } from './shared/apiConfig';
import { ParkingValidationRequestApi } from './shared/api/axios-client';
import { toast } from 'react-toastify';
import { CheckCircleFill } from 'react-bootstrap-icons';

interface HourlyCheckoutFormProps {
    reservationDetails: ReserveInitResp;
    requestId?: string;
    isFree?: boolean;
}

export function StripeHourlyCheckoutForm( props: HourlyCheckoutFormProps ) {
    if ( props.isFree ) {
        return <FreeHourlyCheckoutForm { ...props } />;
    }
    return <StripeHourlyPaidCheckoutForm { ...props } />;
}

function FreeHourlyCheckoutForm( { reservationDetails, requestId }: HourlyCheckoutFormProps ) {
    const maskOptions: PageMaskOpts = {
        showMask: true,
        header:  <div className="h2">Completing Reservation</div>,
        subText: 'Do not close your browser',
    };
    const [, setShowMask] = useAtom( maskAtom );

    const handleSubmit = async ( event: React.FormEvent ) => {
        event.preventDefault();
        setShowMask( maskOptions );

        if ( requestId ) {
            const validationApi = createApiService( ParkingValidationRequestApi );
            const response = await validationApi.completeParkingValidationRequest( { requestId } );
            if ( response.data.error ) {
                toast.error( "Validation Request not found" );
                setShowMask( undefined );
                return;
            }
        }

        const confirmRes = await Api.reservationConfirm( { orderId: reservationDetails.orderId } );
        if ( !confirmRes.isOk ) {
            toast.error( "Could not complete reservation" );
            setShowMask( undefined );
            return;
        }
        window.location.href = connectUrl( "/paid?source=hourly&free=1" );
    };

    return <form onSubmit={handleSubmit}>
        <div
            className="text-center p-4 mb-2 rounded-3"
            style={{
                backgroundColor: "#FFE4F1",
                border: "1px solid #F5A0C8",
            }}
        >
            <CheckCircleFill className="mb-2" style={{ fontSize: "2.5rem", color: "#FF4DB8" }} />
            <div className="fs-5 fw-bold">No payment due today</div>
            <p className="text-muted mb-0 mt-2" style={{ maxWidth: "28rem", margin: "0.5rem auto 0" }}>
                This reservation is fully covered. Confirm below to complete your parking session.
            </p>
        </div>
        <div className="d-flex justify-content-end">
            <Button type="submit" className="mt-3 px-3 btn-main">
                Complete Reservation
            </Button>
        </div>
    </form>;
}

function StripeHourlyPaidCheckoutForm( { reservationDetails, requestId }: HourlyCheckoutFormProps ) {
    const stripe   = useStripe();
    const elements = useElements();

    const maskOptions: PageMaskOpts = {
        showMask: true,
        header:  <div className="h2">Processing Payment</div>,
        subText: 'Do not close your browser',
    };
    const [, setShowMask] = useAtom( maskAtom );

    const handleSubmit = async ( event: React.FormEvent ) => {
        event.preventDefault();

        if ( !stripe || !elements ) {
            return;
        }
        setShowMask( maskOptions );

        if ( requestId ) {
            const validationApi = createApiService( ParkingValidationRequestApi );
            const response = await validationApi.completeParkingValidationRequest( { requestId } );
            if ( response.data.error ) {
                toast.error( "Validation Request not found" );
                setShowMask( undefined );
                return;
            }
        }

        await Api.reservationConfirm( { orderId: reservationDetails.orderId } );
        const result = await stripe.confirmPayment( {
            elements,
            confirmParams: { return_url: connectUrl( "/paid?source=hourly" ) },
        } );

        if ( result.error ) {
            console.log( result.error.message );
        }

        setShowMask( undefined );
    };

    return <form onSubmit={handleSubmit}>
        <PaymentElement />
        <div className="d-flex justify-content-end">
            <Button type="submit" disabled={!stripe} className="mt-3 px-3 btn-main">
                Pay {showMoney( reservationDetails.price )} for {showDuration( reservationDetails.stayDuration )}
            </Button>
        </div>
        {toCents( reservationDetails.price ) > 0 && (
            <div className="alert alert-muted-gold d-flex align-items-start gap-2 mt-3 mb-0" role="alert" style={{ fontSize: "0.85rem" }}>
                <span><b>Note:</b> All payments are final. No refunds or cancellations once a session is confirmed.</span>
            </div>
        )}
    </form>;
}
